import React, { Component } from 'react';
import PaginationComponent from '../components/PaginationComponent';
import ModalPurchase from '../components/ModalPurchase';
import { TICKET_PAYMENT_METHOD, GENERAL_STATUS, COMANDA_STATUS, SHIPPING_TYPE_SHORT } from '../utils/enums';
import { sortClass, nextSortDir, sortAriaSort } from '../utils/table';
import { getTextColorClassGeneral, convertToMoney, formatSaleDateShort } from '../utils/formats';
import { buildPaginationQuery, getPaginatedData, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { getSelectedBranchId } from '../utils/branch';
import { hasActiveSearch, getSearchEmptyMessage } from '../utils/tableState';
import { isCafeteriaMode } from '../utils/appMode';

var config = process.env;
const BAGS_CACHE_KEY = 'purchaseBagsCache';
const BAGS_CACHE_TTL_MS = 15 * 60 * 1000;
const PAGE_CACHE_TTL_MS = 5 * 60 * 1000;
const PAGE_CACHE_MAX_ENTRIES = 20;
const CHANNEL_LABELS = { retail: 'Retail', cafeteria: 'Cafetería', mesas: 'Mesas', eventos: 'Eventos' };
// En modo cafetería la vista de Ventas muestra el salón completo: cafetería + mesas
// (el cover/venta de mesa lo genera el mismo salón). El backend acepta la lista.
const CAFETERIA_CHANNEL_FILTER = 'cafeteria,mesas';
// Canal con el que abre la grilla en cada modo. En retail arranca acotada a retail: abrir en
// "Todos" mezclaba cafetería, mesas y eventos con las ventas del mostrador.
const DEFAULT_CHANNEL = isCafeteriaMode() ? CAFETERIA_CHANNEL_FILTER : 'retail';

class TablePurchaseComponent extends Component {

    constructor() {
        super();
        this.pageCache = new Map();
        this.state = {
            purchaseId: 0,
            status: 0,
            quantityShowRows: 25,
            currentPage: 1,
            totalItems: 0,
            search: '',
            searchInput: '',
            sortBy: 'purchaseId',
            sortDir: 'desc',
            channel: DEFAULT_CHANNEL,
            data: [],
            bags: [],
            loaded: false
        };
    }

    componentDidMount() {
        var id = window.location.search;
        if (id.includes('?id=')) {
            var value = id.replace('?id=', '');
            this.setState({ purchaseId: value });
        }
        this.fetchData();
        this.fetchBags();
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    getCacheKey = ({
        page,
        limit,
        search,
        sortBy,
        sortDir,
        branchId,
        channel,
    }) => JSON.stringify({
        page: Number(page),
        limit: Number(limit),
        search: search || '',
        sortBy: sortBy || '',
        sortDir: sortDir || 'asc',
        branchId: branchId || '',
        channel: channel || '',
    })

    clearPageCache = () => {
        this.pageCache = new Map();
    }

    refreshData = () => {
        this.clearPageCache();
        this.fetchData();
    }

    getCachedBags = () => {
        const rawCache = sessionStorage.getItem(BAGS_CACHE_KEY);
        if (!rawCache) {
            return null;
        }

        try {
            const parsedCache = JSON.parse(rawCache);
            if (!parsedCache || !Array.isArray(parsedCache.bags) || parsedCache.expiresAt <= Date.now()) {
                sessionStorage.removeItem(BAGS_CACHE_KEY);
                return null;
            }

            return parsedCache.bags;
        } catch (error) {
            sessionStorage.removeItem(BAGS_CACHE_KEY);
            return null;
        }
    }

    setCachedBags = (bags) => {
        sessionStorage.setItem(BAGS_CACHE_KEY, JSON.stringify({
            bags: Array.isArray(bags) ? bags : [],
            expiresAt: Date.now() + BAGS_CACHE_TTL_MS,
        }));
    }

    fetchData = ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        sortBy = this.state.sortBy,
        sortDir = this.state.sortDir,
        channel = this.state.channel,
        showLoader = true,
    } = {}) => {
        const selectedBranchId = getSelectedBranchId();
        const cacheKey = this.getCacheKey({
            page,
            limit,
            search,
            sortBy,
            sortDir,
            branchId: selectedBranchId,
            channel,
        });
        const cachedEntry = this.pageCache.get(cacheKey);
        const cachedPage = cachedEntry && cachedEntry.expiresAt > Date.now() ? cachedEntry.value : null;
        if (cachedEntry && !cachedPage) {
            this.pageCache.delete(cacheKey);
        }

        if (cachedPage) {
            this.setState({
                ...cachedPage,
                search,
                sortBy,
                sortDir,
                channel,
                loaded: true,
            }, () => {
                if (this.state.purchaseId > 0 && document.getElementById('button-show-dialog')) {
                    document.getElementById('button-show-dialog').click();
                }
            });
            return;
        }

        if (showLoader) {
            this.setState({ loaded: false });
        }
        const query = buildPaginationQuery({
            page,
            limit,
            search,
            sortBy,
            sortDir,
            extraParams: {
                branchId: selectedBranchId || undefined,
                channel: channel || undefined,
            },
        });
        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}purchase?${query}`, request)
        .then(results => results.json())
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                const paginatedData = getPaginatedData(data);
                const nextState = {
                    data: paginatedData.items || [],
                    quantityShowRows: paginatedData.limit || Number(limit),
                    currentPage: paginatedData.page || Number(page),
                    totalItems: paginatedData.total || 0,
                    search,
                    sortBy,
                    sortDir,
                    channel,
                    loaded: true
                };

                if (this.pageCache.size >= PAGE_CACHE_MAX_ENTRIES) {
                    const oldestKey = this.pageCache.keys().next().value;
                    this.pageCache.delete(oldestKey);
                }
                this.pageCache.delete(cacheKey);
                this.pageCache.set(cacheKey, {
                    value: {
                        data: nextState.data,
                        quantityShowRows: nextState.quantityShowRows,
                        currentPage: nextState.currentPage,
                        totalItems: nextState.totalItems,
                    },
                    expiresAt: Date.now() + PAGE_CACHE_TTL_MS,
                });

                this.setState(nextState, () => {
                    if (this.state.purchaseId > 0 && document.getElementById('button-show-dialog')) {
                        document.getElementById('button-show-dialog').click();
                    }
                });
            }
        });
    }

    fetchBags = () => {
        const cachedBags = this.getCachedBags();
        if (cachedBags != null) {
            this.setState({
                bags: cachedBags,
            });
            return;
        }

        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'accessory/bags', request)
        .then(results => results.json())
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                this.setCachedBags(data.data);
                this.setState({
                    bags: data.data
                });
            }
        });
    }

    renderEmptyState = () => {
        return (
            <div
                style={{
                    background: '#ffffff',
                    borderRadius: '0.625rem',
                    boxShadow: '0 .125rem .625rem rgba(90,97,105,.12)',
                    border: '1px solid #e1e5eb',
                    minHeight: '320px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                }}
            >
                <div style={{ textAlign: 'center', maxWidth: '420px' }}>
                    <div
                        style={{
                            width: '72px',
                            height: '72px',
                            margin: '0 auto 1rem',
                            borderRadius: '50%',
                            background: '#f5f6f8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <i className="material-icons" style={{ fontSize: '34px', color: '#818ea3' }}>shopping_cart</i>
                    </div>
                    <h4 style={{ marginBottom: '0.75rem', color: '#3d5170', fontWeight: 500 }}>
                        Todavía no hay ventas para mostrar
                    </h4>
                    <p style={{ margin: 0, color: '#818ea3', lineHeight: 1.6 }}>
                        Cuando se registren ventas, acá vas a ver el listado con estado, origen,
                        forma de pago, envío y detalle de cada compra.
                    </p>
                </div>
            </div>
        );
    }

    renderLoader = () => {
        return (
            <div
                style={{
                    background: '#ffffff',
                    borderRadius: '0.625rem',
                    boxShadow: '0 .125rem .625rem rgba(90,97,105,.12)',
                    border: '1px solid #e1e5eb',
                    minHeight: '320px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                }}
            >
                <div className="containerLoader" style={{ display: 'block', position: 'static', width: '100%' }}>
                    <div className="loader"></div>
                </div>
            </div>
        );
    }

    loadData = () => {
        if (!this.state.loaded) {
            return <tbody></tbody>;
        }

        var items = [];
        var modals = [];
        this.state.data.forEach((item, index) => {
            items.push(this.drawRow(item, index));
            modals.push(this.createModalAddBag(item.purchaseId));
        });
        if (items.length === 0) {
            items.push(
                <tr key="empty">
                    <td colSpan={this.getColumns().length} style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                        {getSearchEmptyMessage(this.state.searchInput || this.state.search)}
                    </td>
                </tr>
            );
        }
        return (
            <tbody>{items}{modals}</tbody>
        );
    }

    onClickModal = (purchaseId, status) => {
        this.setState({ purchaseId: purchaseId, status: status });
    }

    getItemsBags = () => {
        return this.state.bags.map(item => (
            <option value={item.code} key={item.code}>{item.name}</option>
        ));
    }

    onClickAddBag = (purchaseId) => {
        var value = document.getElementById(`bag-${purchaseId}`).value;
        var request = {
            method: 'POST',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                purchaseId: purchaseId,
                bag: value
            })
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'purchase/bag', request)
        .then(results => results.json())
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                this.refreshData();
            }
        });
    }

    createModalAddBag = (purchaseId) => {
        return (
            <div className="modal fade" id={`modal-add-bag-${purchaseId}`} role="dialog" key={`bag-${purchaseId}`}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title" style={{ textAlign: 'center' }}>Agregar bolsa</h4>
                        </div>
                        <div className="modal-body">
                            <p>Seleccione una bolsa para esta compra</p>
                            <select id={`bag-${purchaseId}`} className="custom-select">
                                {this.getItemsBags()}
                            </select>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="mb-2 mr-1 btn btn-outline-secondary btn-sm"
                                data-dismiss="modal">
                                Cancelar
                            </button>
                            <button
                                className="mb-2 mr-1 btn btn-primary btn-sm"
                                data-dismiss="modal"
                                onClick={() => this.onClickAddBag(purchaseId)}>
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Columnas de la grilla: una sola definición de la que salen el <thead>, las celdas y el
     * colSpan de la fila vacía. Antes eran tres listas paralelas que había que mantener
     * sincronizadas a mano.
     *
     * `width` es un peso relativo, no un porcentaje: se normaliza al renderizar, así que al
     * mostrar/ocultar una columna el resto se reparte el ancho sin tener que reajustar números.
     */
    getColumns = () => {
        const columns = [
            {
                key: 'purchaseId', label: 'ID', sortBy: 'purchaseId', width: 6,
                render: (item) => <td tabIndex="0" className="sorting_1" key="purchaseId">{item.purchaseId}</td>,
            },
            {
                key: 'status', label: 'Estado', sortBy: 'status', width: 16,
                render: (item) => (
                    <td className={getTextColorClassGeneral(item.status)} key="status">
                        {item.channel === 'cafeteria' && item.status != 9 && item.kitchenOrderStatus && item.kitchenOrderStatus !== 'cancelled'
                            ? `Pagada · ${COMANDA_STATUS[item.kitchenOrderStatus] || 'Comanda pendiente'}`
                            : GENERAL_STATUS[item.status]}
                    </td>
                ),
            },
            {
                key: 'client', label: 'Cliente', sortBy: 'firstName', width: 16,
                render: (item) => <td key="client">{item.firstName} {item.lastName}</td>,
            },
            {
                key: 'date', label: 'Fecha', sortBy: 'date', width: 11,
                // nowrap: si no, '14/jul 00:24' parte en dos renglones y engorda todas las filas.
                render: (item) => <td key="date" style={{ whiteSpace: 'nowrap' }}>{formatSaleDateShort(item.date)}</td>,
            },
            {
                key: 'originator', label: 'Origen', sortBy: 'originator', width: 9,
                render: (item) => <td key="originator">{item.originator}</td>,
            },
            {
                key: 'channel', label: 'Canal', sortBy: 'channel', width: 8,
                render: (item) => <td key="channel">{CHANNEL_LABELS[item.channel] || (item.channel || 'Retail')}</td>,
            },
            {
                key: 'paymentType', label: 'Pago', sortBy: 'paymentType', width: 12,
                render: (item) => <td key="paymentType">{TICKET_PAYMENT_METHOD[item.paymentType]}</td>,
            },
            {
                key: 'shippingType', label: 'Envío', sortBy: 'shippingType', width: 11,
                // '—' cuando la venta no tiene registro de envío (mesas, eventos): no hay logística.
                render: (item) => <td key="shippingType">{SHIPPING_TYPE_SHORT[item.shippingType] || '—'}</td>,
            },
            {
                key: 'totalPrice', label: 'Total', sortBy: 'totalPrice', width: 10,
                // nowrap: el importe nunca debe partirse entre el '$' y los dígitos.
                render: (item) => <td key="totalPrice" style={{ whiteSpace: 'nowrap' }}>$ {convertToMoney(item.totalPrice)}</td>,
            },
            {
                key: 'actions', label: 'Acciones', sortBy: null, width: 9,
                render: (item) => this.renderActionsCell(item),
            },
        ];

        return this.shouldShowChannelColumn()
            ? columns
            : columns.filter((column) => column.key !== 'channel');
    }

    /**
     * La columna Canal sólo aporta cuando el filtro puede devolver más de un canal. Con el
     * filtro en un canal puntual —el default en modo retail— repetía el mismo valor en las 25
     * filas. En modo cafetería el filtro por defecto es 'cafeteria,mesas', que sí mezcla dos.
     */
    shouldShowChannelColumn = () => {
        const channel = this.state.channel || '';
        return channel === '' || channel.includes(',');
    }

    renderHeaderCells = () => {
        const columns = this.getColumns();
        // Los anchos se normalizan sobre las columnas efectivamente visibles: al esconder Canal,
        // su espacio se reparte solo en vez de dejar la tabla sumando != 100%.
        const totalWidth = columns.reduce((sum, column) => sum + column.width, 0);
        return columns.map((column) => {
            const width = { width: `${((column.width / totalWidth) * 100).toFixed(2)}%` };
            if (!column.sortBy) {
                return <th key={column.key} rowSpan="1" colSpan="1" style={width}>{column.label}</th>;
            }
            return (
                <th
                    {...this.sortableHeader(column.sortBy)}
                    key={column.key}
                    aria-controls="DataTables_Table_0"
                    rowSpan="1"
                    colSpan="1"
                    style={width}>{column.label}</th>
            );
        });
    }

    drawRow = (item, index) => {
        return (
            <tr role="row" className="odd" key={item.purchaseId} style={{ background: index % 2 == 0 ? '#ffffff;' : '#fcfcfc' }}>
                {this.getColumns().map((column) => column.render(item))}
            </tr>
        );
    }

    renderActionsCell = (item) => {
        return (
                <td key="actions">
                    {item.status != 10 ? (
                        <div className="btn-group btn-group-sm" role="group" aria-label="Table row actions">
                            {/* ENI-97: la bolsa es de la venta de juegos; no aplica a cafetería ni a mesas (cover). */}
                            <button
                                style={{ display: (item.hasBag || item.channel === 'cafeteria' || item.channel === 'mesas') ? 'none' : 'block' }}
                                data-toggle="modal"
                                data-target={`#modal-add-bag-${item.purchaseId}`}
                                type="button"
                                className="btn btn-white"><i className="material-icons">work_outline</i>
                            </button>
                            <button
                                data-toggle="modal"
                                data-target="#modal-view"
                                onClick={() => this.onClickModal(item.purchaseId, item.status)}
                                type="button"
                                className="btn btn-white"><i className="material-icons">search</i>
                            </button>
                        </div>
                    ) : (
                        <div className="btn-group btn-group-sm" role="group" aria-label="Table row actions">
                            <button
                                data-toggle="modal"
                                data-target="#modal-view"
                                onClick={() => this.onClickModal(item.purchaseId, item.status)}
                                type="button"
                                className="btn btn-white"><i className="material-icons">add_shopping_cart</i>
                            </button>
                        </div>
                    )}
                </td>
        );
    }

    onChangeShowRows = () => {
        const itemSelected = Number(document.getElementById('select_rows').value);
        this.fetchData({ page: 1, limit: itemSelected });
    }

    onChangeChannel = (value) => {
        // Si estaba ordenando por Canal y el filtro nuevo esconde esa columna, el orden quedaría
        // aplicado por una columna que ya no se ve (y sin cabezal para revertirlo): vuelve al default.
        const channelColumnVisible = value === '' || String(value).includes(',');
        const sortBy = (!channelColumnVisible && this.state.sortBy === 'channel') ? 'purchaseId' : this.state.sortBy;
        const sortDir = sortBy === this.state.sortBy ? this.state.sortDir : 'desc';
        this.setState({ channel: value });
        this.fetchData({ page: 1, channel: value, sortBy, sortDir });
    }

    // En cafetería el combo acota dentro del salón (cafetería+mesas); en retail es el
    // filtro global de todos los canales.
    renderChannelOptions = () => {
        if (isCafeteriaMode()) {
            return (
                <React.Fragment>
                    <option value={CAFETERIA_CHANNEL_FILTER}>Cafetería y mesas</option>
                    <option value="cafeteria">Solo cafetería</option>
                    <option value="mesas">Solo mesas</option>
                </React.Fragment>
            );
        }
        return (
            <React.Fragment>
                <option value="">Todos</option>
                <option value="retail">Retail</option>
                <option value="cafeteria">Cafetería</option>
                <option value="mesas">Mesas</option>
                <option value="eventos">Eventos</option>
                <option value="membresias">Membresías</option>
            </React.Fragment>
        );
    }

    // Barra de controles (cantidad de filas, canal, buscador). Va tanto sobre la tabla como
    // sobre el empty state: sin el selector de Canal a la vista, una sucursal sin ventas del
    // canal por defecto quedaba encerrada sin forma de cambiarlo.
    renderToolbar = () => {
        return (
            <React.Fragment>
                <div className="dataTables_length" id="DataTables_Table_0_length">
                    <label>Show
                    <select id="select_rows" name="DataTables_Table_0_length" aria-controls="DataTables_Table_0" value={this.state.quantityShowRows} onChange={() => this.onChangeShowRows()}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    entries</label>
                    <label style={{ marginLeft: '1.5rem' }}>Canal&nbsp;
                    <select value={this.state.channel} onChange={(e) => this.onChangeChannel(e.target.value)}>
                        {this.renderChannelOptions()}
                    </select>
                    </label>
                </div>
                <div id="DataTables_Table_0_filter" className="dataTables_filter">
                    <label>Search:
                    <input id="input_search" type="search" className="" placeholder="" aria-controls="DataTables_Table_0" value={this.state.searchInput} onChange={(e) => this.onChangeInput(e.target.value)}/>
                    </label>
                </div>
            </React.Fragment>
        );
    }

    onChangeInput = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchData({ page: 1, search, showLoader: false });
        });
    }

    orderData = (column) => {
        this.fetchData({
            page: 1,
            sortBy: column,
            sortDir: nextSortDir(column, this.state.sortBy, this.state.sortDir),
        });
    }

    // Props de una cabecera ordenable: la clase y el aria-sort se derivan del state, así que
    // sobreviven al remonte que provoca el loader.
    sortableHeader = (column) => ({
        onClick: () => this.orderData(column),
        className: sortClass(column, this.state.sortBy, this.state.sortDir),
        'aria-sort': sortAriaSort(column, this.state.sortBy, this.state.sortDir),
        tabIndex: '0',
    })

    render() {
        if (!this.state.loaded) {
            return (
                <div>
                    {this.renderLoader()}
                    <ModalPurchase purchaseId={this.state.purchaseId} status={this.state.status} onRefresh={this.refreshData} onClose={() => this.setState({ purchaseId: 0 })} />
                    <button
                        id="button-show-dialog"
                        style={{ display: 'none' }}
                        data-toggle="modal"
                        data-target="#modal-view"
                        onClick={() => this.onClickModal(this.state.purchaseId)}
                        type="button"
                        className="btn btn-white"><i className="material-icons">search</i>
                    </button>
                </div>
            );
        }

        // El empty state amable es para "todavía no hay ventas", no para "el filtro no trajo
        // nada": sólo aplica si el canal sigue siendo el que la grilla trae por defecto.
        // Los controles se renderizan IGUAL: como el canal por defecto ya no es "Todos", una
        // sucursal que sólo vende por cafetería/mesas cae acá, y sin el selector de Canal a la
        // vista quedaba encerrada sin forma de llegar a sus ventas.
        if (this.state.totalItems === 0 && !hasActiveSearch(this.state.searchInput, this.state.search) && this.state.channel === DEFAULT_CHANNEL) {
            return (
                <div className="dataTables_wrapper no-footer">
                    {this.renderToolbar()}
                    {/* clear: los controles de la barra flotan (left/right), y sin esto la tarjeta
                        del empty state se acomoda al lado en vez de ocupar el ancho. */}
                    <div style={{ clear: 'both' }}>
                        {this.renderEmptyState()}
                    </div>
                    <ModalPurchase purchaseId={this.state.purchaseId} status={this.state.status} onRefresh={this.refreshData} onClose={() => this.setState({ purchaseId: 0 })} />
                    <button
                        id="button-show-dialog"
                        style={{ display: 'none' }}
                        data-toggle="modal"
                        data-target="#modal-view"
                        onClick={() => this.onClickModal(this.state.purchaseId)}
                        type="button"
                        className="btn btn-white"><i className="material-icons">search</i>
                    </button>
                </div>
            );
        }

        return (
            <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                {this.renderToolbar()}

                <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info">
                    <thead>
                        <tr role="row">
                            {this.renderHeaderCells()}
                        </tr>
                    </thead>
                    {this.loadData()}
                </table>

                <ModalPurchase purchaseId={this.state.purchaseId} status={this.state.status} onRefresh={this.refreshData} onClose={() => this.setState({ purchaseId: 0 })} />

                <button
                    id="button-show-dialog"
                    style={{ display: 'none' }}
                    data-toggle="modal"
                    data-target="#modal-view"
                    onClick={() => this.onClickModal(this.state.purchaseId)}
                    type="button"
                    className="btn btn-white"><i className="material-icons">search</i>
                </button>

                <PaginationComponent
                    totalItems={this.state.totalItems}
                    quantityRows={this.state.quantityShowRows}
                    currentPage={this.state.currentPage}
                    onPageChange={(page) => this.fetchData({ page })}
                />
            </div>
        );
    }
}

export default TablePurchaseComponent;
